import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  LockClosedIcon,
  EyeIcon,
  DocumentCheckIcon,
} from '@heroicons/react/24/outline';
import {
  PageShell,
  PremiumHero,
  PremiumSection,
  SectionHeader,
  FeatureGrid,
  GlassCard,
  PremiumCTA,
} from '../premium/PremiumPageUI';

const services = [
  { title: 'Threat Detection & Response', description: 'Real-time monitoring, SIEM integration, and automated incident response to minimize breach impact.', icon: EyeIcon },
  { title: 'Penetration Testing', description: 'Ethical hacking and vulnerability assessments to harden systems before attackers find weaknesses.', icon: LockClosedIcon },
  { title: 'Compliance & Risk', description: 'GDPR, HIPAA, PCI-DSS, and ISO 27001 alignment with continuous audit readiness.', icon: DocumentCheckIcon },
];

const threatData = [
  { country: 'United States', type: 'Ransomware', attacks: 1243, severity: 'High' },
  { country: 'China', type: 'DDoS', attacks: 987, severity: 'Medium' },
  { country: 'Russia', type: 'Phishing', attacks: 876, severity: 'High' },
  { country: 'Brazil', type: 'Malware', attacks: 543, severity: 'Medium' },
  { country: 'India', type: 'Data Breach', attacks: 432, severity: 'High' },
];

const securityTips = [
  'Use strong, unique passwords and enable MFA everywhere',
  'Keep all software and systems patched and updated',
  'Train staff to recognize phishing and social engineering',
  'Implement zero-trust network access policies',
  'Maintain encrypted backups with tested recovery plans',
];

const severityClass = (s) =>
  s === 'High'
    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
    : s === 'Medium'
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';

export default function Cybersecurity() {
  const [activeTab, setActiveTab] = useState('threat');
  const [threatLevel, setThreatLevel] = useState('medium');
  const [securityScore, setSecurityScore] = useState(72);
  const [showTips, setShowTips] = useState(false);

  const runAssessment = () => {
    setSecurityScore(Math.floor(Math.random() * 25) + 65);
    setThreatLevel(['low', 'medium', 'high'][Math.floor(Math.random() * 3)]);
  };

  useEffect(() => {
    const t = setTimeout(runAssessment, 1200);
    return () => clearTimeout(t);
  }, []);

  const tabs = [
    { id: 'threat', label: 'Threat Detection' },
    { id: 'assessment', label: 'Security Assessment' },
    { id: 'compliance', label: 'Compliance' },
  ];

  return (
    <PageShell>
      <PremiumHero
        badge="Enterprise Security"
        title="Advanced"
        highlight="Cybersecurity"
        subtitle="Zero-trust architectures, threat intelligence, and compliance frameworks engineered to protect your business in an evolving threat landscape."
        variant="security"
        stats={[
          { value: '24/7', label: 'SOC Monitoring' },
          { value: '99.9%', label: 'Uptime SLA' },
          { value: '0', label: 'Data Breaches' },
          { value: 'SOC2', label: 'Aligned' },
        ]}
        actions={[
          { label: 'Security Assessment', primary: true, onClick: () => document.getElementById('dashboard')?.scrollIntoView({ behavior: 'smooth' }) },
          { label: 'Contact Experts', href: '/contact' },
        ]}
      />

      <PremiumSection>
        <SectionHeader badge="Services" title="Comprehensive" highlight="Protection" />
        <FeatureGrid items={services} columns={3} />
      </PremiumSection>

      <PremiumSection id="dashboard" alt>
        <SectionHeader badge="Live Intel" title="Security" highlight="Command Center" subtitle="Interactive dashboard preview—monitor threats, assess posture, and track compliance in real time." />
        <GlassCard className="overflow-hidden premium-border-glow" hover={false}>
          <div className="border-b border-slate-200/80 dark:border-gray-700 flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#2563EB] border-b-2 border-[#2563EB] bg-[#2563EB]/5'
                    : 'text-slate-500 dark:text-gray-400 hover:text-[#2563EB]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="p-6 md:p-8">
            {activeTab === 'threat' && (
              <div className="space-y-6">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-sm font-medium text-slate-700 dark:text-gray-300">Threat Level</span>
                  <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-bold capitalize ${severityClass(threatLevel === 'high' ? 'High' : threatLevel === 'medium' ? 'Medium' : 'Low')}`}>
                    <span className={`w-2 h-2 rounded-full ${threatLevel === 'high' ? 'bg-red-500' : threatLevel === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'} animate-pulse`} />
                    {threatLevel}
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: 'Suspicious Logins', val: 12, color: 'text-red-500' },
                    { label: 'Malware Detected', val: 3, color: 'text-amber-500' },
                    { label: 'Phishing Attempts', val: 8, color: 'text-red-500' },
                    { label: 'Data Exfiltration', val: 0, color: 'text-emerald-500' },
                  ].map((m) => (
                    <div key={m.label} className="hero-glass-tag rounded-xl p-4 text-center">
                      <p className={`text-2xl font-bold ${m.color}`}>{m.val}</p>
                      <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">{m.label}</p>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => setShowTips(!showTips)} className="text-sm font-semibold text-[#2563EB] hover:underline">
                  {showTips ? 'Hide' : 'Show'} Security Tips
                </button>
                {showTips && (
                  <ul className="grid md:grid-cols-2 gap-2 text-sm text-slate-600 dark:text-gray-300">
                    {securityTips.map((tip) => (
                      <li key={tip} className="flex gap-2"><ShieldCheckIcon className="w-4 h-4 text-[#2563EB] flex-shrink-0" />{tip}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {activeTab === 'assessment' && (
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between text-sm font-semibold mb-2">
                    <span>Security Score</span>
                    <span className="text-[#2563EB]">{securityScore}%</span>
                  </div>
                  <div className="h-3 rounded-full bg-slate-200 dark:bg-gray-700 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${securityScore}%` }} className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-cyan-500" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {[{ l: 'Critical', v: 3, c: 'text-red-500' }, { l: 'Medium', v: 7, c: 'text-amber-500' }, { l: 'Low', v: 12, c: 'text-emerald-500' }].map((x) => (
                    <div key={x.l} className="hero-glass-tag rounded-xl p-4 text-center">
                      <p className={`text-2xl font-bold ${x.c}`}>{x.v}</p>
                      <p className="text-xs text-slate-500">{x.l}</p>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={runAssessment} className="px-6 py-2.5 bg-gradient-to-r from-[#2563EB] to-blue-500 text-white text-sm font-semibold rounded-xl">
                  Run New Assessment
                </button>
              </div>
            )}
            {activeTab === 'compliance' && (
              <div className="space-y-4">
                {[
                  { name: 'GDPR', status: 'Compliant', ok: true },
                  { name: 'HIPAA', status: 'Partial', ok: false },
                  { name: 'PCI DSS', status: 'Compliant', ok: true },
                  { name: 'ISO 27001', status: 'In Progress', ok: false },
                ].map((c) => (
                  <div key={c.name} className="flex justify-between items-center p-4 rounded-xl hero-glass-tag">
                    <span className="font-medium">{c.name}</span>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${c.ok ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>
                      {c.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </GlassCard>
      </PremiumSection>

      <PremiumSection>
        <SectionHeader badge="Threat Intel" title="Global" highlight="Threat Map" />
        <GlassCard className="p-6 md:p-8" hover={false}>
          <div className="h-48 md:h-64 rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-[#1e3a8a] flex items-center justify-center relative overflow-hidden mb-8">
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full bg-cyan-400/80"
                style={{ left: `${10 + (i * 7) % 80}%`, top: `${15 + (i * 11) % 70}%` }}
                animate={{ opacity: [0.3, 1, 0.3], scale: [1, 1.4, 1] }}
                transition={{ duration: 2 + i * 0.3, repeat: Infinity }}
              />
            ))}
            <p className="relative text-cyan-200/80 text-sm font-medium tracking-widest uppercase">Live Threat Visualization</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="pb-3 pr-6">Country</th>
                  <th className="pb-3 pr-6">Attack Type</th>
                  <th className="pb-3 pr-6">Volume</th>
                  <th className="pb-3">Severity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                {threatData.map((row) => (
                  <tr key={row.country} className="text-slate-700 dark:text-gray-300">
                    <td className="py-4 font-medium">{row.country}</td>
                    <td className="py-4">{row.type}</td>
                    <td className="py-4">{row.attacks.toLocaleString()}</td>
                    <td className="py-4"><span className={`px-2.5 py-1 rounded-full text-xs font-bold ${severityClass(row.severity)}`}>{row.severity}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      </PremiumSection>

      <PremiumCTA
        title="Ready to Secure Your Business?"
        subtitle="Our cybersecurity experts will assess your posture and design a defense strategy tailored to your industry."
        primary={{ label: 'Get Started', href: '/contact' }}
        secondary={{ label: 'Academy Programs', href: '/academy' }}
      />
    </PageShell>
  );
}
